import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';

import { SharedService } from '@services/shared.service';

/**
 * Reusable .glb preview popup. Renders the model in-app instead of linking to the file —
 * the vault serves models as application/octet-stream, so a plain link only downloads them.
 *
 * Usage: <app-model-preview [(visible)]="open" [url]="url" [title]="name" [size]="bytes" />
 */
@Component({
  selector: 'app-model-preview',
  standalone: true,
  imports: [CommonModule, TranslateModule, NzModalModule, NzToolTipModule],
  templateUrl: './model-preview.component.html',
  styleUrls: ['./model-preview.component.scss'],
  // <model-viewer> is a web component, not an Angular one.
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ModelPreviewComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() url = '';
  @Input() title = '';
  @Input() size = 0;

  @Output() visibleChange = new EventEmitter<boolean>();

  private sharedService = inject(SharedService);
  private cdRef = inject(ChangeDetectorRef);

  isViewerReady = false;
  previewError = '';
  autoRotate = true;
  // blob: URL handed to the viewer — `url` stays the real link shown in the footer.
  objectUrl = '';

  private viewerLoaded = false;

  ngOnChanges(changes: SimpleChanges): void {
    // Runs after every input is set, so `url` is never stale when `visible` flips on.
    if (changes['visible']) {
      this.visible ? this.load() : this.reset();
    }
  }

  ngOnDestroy(): void {
    this.releaseObjectUrl();
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.reset();
  }

  copyUrl(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.sharedService.copyToClipboard(this.url);
  }

  onViewerError(event: any): void {
    const detail = event?.detail;
    console.error('[model-preview] model-viewer error:', detail ?? event);
    this.previewError = detail?.sourceError?.message || detail?.type || 'Render error';
    this.cdRef.detectChanges();
  }

  formatSize(bytes: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  private async load(): Promise<void> {
    this.isViewerReady = false;
    this.previewError = '';
    this.autoRotate = true;
    this.releaseObjectUrl();

    if (!this.url) {
      this.previewError = 'No model URL';
      return;
    }

    try {
      if (!this.viewerLoaded) {
        // Loaded on demand so the (heavy) viewer stays out of the main bundle.
        await import('@google/model-viewer');
        this.viewerLoaded = true;
      }

      // Fetch the file ourselves and hand the viewer a same-origin blob: URL. The vault link
      // 303-redirects to Google Storage, and three.js's internal loader fails on that
      // cross-origin redirect. A plain fetch (no auth headers — those would break the
      // redirect to Storage) keeps it a simple CORS request, which the vault allows.
      const response = await fetch(this.url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`.trim());
      }

      const blob = await response.blob();
      this.objectUrl = URL.createObjectURL(blob);
      this.isViewerReady = true;
    } catch (err: any) {
      console.error('[model-preview] failed to load model:', err);
      this.previewError = err?.message || 'Unknown error';
    }

    this.cdRef.detectChanges();
  }

  private reset(): void {
    this.isViewerReady = false;
    this.previewError = '';
    this.releaseObjectUrl();
  }

  private releaseObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = '';
    }
  }
}
